import { Component, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { LanguageService } from 'src/app/services/language/language.service';

interface DocumentTypeStats {
  type: string;
  count: number;
}

interface GroupedDocument {
  id: string;
  original_filename: string;
  type: string;
  user: string;
  created_at: string;
}

interface DocumentGroup {
  group: string;
  documents: GroupedDocument[];
}

@Component({
    selector: 'app-document-overview',
    templateUrl: './document-overview.component.html',
    styleUrls: ['./document-overview.component.scss'],
    standalone: false
})
export class DocumentOverviewComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * El tab que contiene esta vista. ECharts se inicializa con el tamano que
   * tenga el contenedor en ese instante y, si el tab aun no es visible, se queda
   * clavado en su tamano por defecto (400px) ignorando el CSS. Al activarse hay
   * que pedirle explicitamente que recalcule.
   */
  @Input() active = true;

  private chartInstance: { resize: () => void } | null = null;
  private themeSubscription?: Subscription;

  isLoading = true;
  errorMessage = '';
  documentStats: DocumentTypeStats[] = [];
  groupedDocuments: DocumentGroup[] = [];
  selectedType: string | null = null;
  chartOptions: any;
  chartData: any[] = [];
  chartColors: string[] = ['#7c4dff', '#22c55e', '#f59e0b', '#e53935', '#8e24aa'];

  @Output() typeSelected = new EventEmitter<string>();

  constructor(
    private readonly documentService: DocumentService,
    public themeService: ThemeService,
    public languageService: LanguageService,
    private readonly snackBar: MatSnackBar,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  ngOnInit(): void {
    this.loadStats();
    this.subscribeToThemeChanges();
  }

  loadStats() {
    this.isLoading = true;
    this.documentService.getDocumentStats().subscribe({
      next: (stats) => {
        this.documentStats = stats;
        this.setupChart();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = this.translate('documents.error_loading');
        this.isLoading = false;
      }
    });
  }
  

  setupChart() {
    const total = this.documentStats.reduce((sum, d) => sum + d.count, 0);
      const isSmall = this.isSmallScreen();
    const isDark = this.themeService.getCurrentTheme() === 'dark';
    const labelColor = isDark ? '#e7eaf4' : '#1f2937';
    const borderColor = isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.06)';
    const labelLineColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)';
    const pieGeometry = this.getPieGeometry(isSmall);
    this.chartData = this.documentStats.map((d, i) => ({
      value: d.count,
      name: d.type,
      itemStyle: { color: this.chartColors[i % this.chartColors.length] }
    }));
    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => this.formatTooltip(params, total)
      },
      legend: this.buildLegendOptions(isSmall, labelColor),
      series: [
        {
          name: this.translate('documents.types_distribution'),
          type: 'pie',
          radius: pieGeometry.radius,
          center: pieGeometry.center,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: borderColor,
            borderWidth: 2
          },
          label: this.buildLabelOptions(isSmall, labelColor),
          labelLine: { show: !isSmall, length: 18, length2: 14, lineStyle: { color: labelLineColor } },
          labelLayout: { hideOverlap: true },
          emphasis: {
            label: {
              show: true,
              fontSize: isSmall ? 16 : 20,
              fontWeight: 'bold'
            }
          },
          data: this.chartData
        }
      ]
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && this.active) {
      this.resizeChart();
    }
  }

  /**
   * ECharts pinta sobre un canvas, no con CSS: el color de las etiquetas queda
   * congelado en las opciones. Sin esta suscripcion, al cambiar de tema el
   * grafico conservaba los colores del tema anterior hasta recargar la pagina.
   */
  subscribeToThemeChanges(): void {
    this.themeSubscription = this.themeService.currentTheme$
      .pipe(skip(1))
      .subscribe(() => {
        if (this.documentStats.length) {
          this.setupChart();
        }
      });
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
  }

  /** ngx-echarts entrega aqui la instancia real del grafico. */
  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
  }

  /**
   * Se espera a que termine la animacion de entrada del tab: durante la
   * transicion el contenedor todavia no tiene su tamano definitivo.
   */
  private resizeChart(): void {
    setTimeout(() => this.chartInstance?.resize(), 300);
  }

  private isSmallScreen(): boolean {
    return globalThis.window?.matchMedia?.('(max-width: 480px)').matches ?? false;
  }

  private formatTooltip(params: any, total: number): string {
    const percent = total ? ((params.value / total) * 100).toFixed(1) : 0;
    return `${this.translateType(params.name)}: <b>${params.value}</b> (${percent}%)`;
  }

  private buildLegendOptions(isSmall: boolean, labelColor: string) {
    return {
      orient: isSmall ? 'horizontal' : 'vertical',
      left: isSmall ? 'center' : 0,
      top: isSmall ? undefined : 'center',
      bottom: isSmall ? 0 : undefined,
      itemWidth: isSmall ? 14 : 22,
      itemHeight: isSmall ? 12 : 18,
      textStyle: {
        fontSize: isSmall ? 12 : 16,
        fontWeight: 'bold',
        width: isSmall ? undefined : 160,
        overflow: isSmall ? 'truncate' : 'break',
        color: labelColor
      },
      data: this.documentStats.map(d => this.translateType(d.type)),
      show: !isSmall
    };
  }

  private buildLabelOptions(isSmall: boolean, labelColor: string) {
    return {
      show: !isSmall,
      position: 'outside',
      formatter: (params: any) => this.formatLabel(params.name),
      fontWeight: 'bold',
      fontSize: isSmall ? 12 : 16,
      width: isSmall ? 88 : 120,
      overflow: isSmall ? 'truncate' : 'break',
      color: labelColor
    };
  }

  private formatLabel(name: string): string {
    return name.length > 16 ? name.replaceAll(/(.{1,16})(\s|$)/g, '$1\n') : name;
  }

  private getPieGeometry(isSmall: boolean): { radius: [string, string]; center: [string, string] } {
    if (isSmall) {
      return {
        radius: ['46%', '78%'],
        center: ['50%', '54%']
      };
    }

    return {
      radius: ['52%', '86%'],
      center: ['50%', '56%']
    };
  }

  onChartClick(event: any) {
    this.frontendLogger.info(`[DocumentOverviewComponent][onChartClick] event: ${JSON.stringify(event)}, selectedType: ${this.selectedType}`);
    // Permitir deseleccionar si se hace click en el mismo tipo
    if (this.selectedType === event.name) {
      this.resetTypeFilter();
    } else {
      this.selectedType = event.name;
      this.frontendLogger.info(`[DocumentOverviewComponent][onChartClick] Emitting typeSelected: ${event.name}`);
      this.typeSelected.emit(event.name);
      this.snackBar.open(this.translate('documents.selected_type') + ': ' + this.translateType(event.name), '', { duration: 2000 });
    }
  }

  onSidebarTypeClick(type: string) {
    this.frontendLogger.info(`[DocumentOverviewComponent][onSidebarTypeClick] type: ${type}, selectedType: ${this.selectedType}`);
    // Permitir deseleccionar si se hace click en el mismo tipo
    if (this.selectedType === type) {
      this.resetTypeFilter();
    } else {
      this.selectedType = type;
      this.frontendLogger.info(`[DocumentOverviewComponent][onSidebarTypeClick] Emitting typeSelected: ${type}`);
      this.typeSelected.emit(type);
      this.snackBar.open(this.translate('documents.selected_type') + ': ' + this.translateType(type), '', { duration: 2000 });
    }
  }

  resetTypeFilter() {
    this.frontendLogger.info('[DocumentOverviewComponent][resetTypeFilter] Resetting type filter');
    this.selectedType = null;
    this.typeSelected.emit(undefined);
    this.setupChart();
  }

  translate(key: string): string {
    return this.languageService.translate(key);
  }

  translateType(type: string): string {
    const key = 'documents.type_' + type.toLowerCase();
    return this.translate(key) || type;
  }
}
